import { FlagIcon, MessageSquareIcon } from 'lucide-react'
import pluralize from 'pluralize'

import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'
import { Questionnaire, QuestionnaireType } from '@/gen/questionnaire/v2/model_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { useAssessmentAnalytics } from '@/pages/assessment/analytics'
import { ASSESSMENT_TABS } from '@/pages/assessment/assessment-tabs/assessment-tabs'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

import { OverviewCard } from './overview-card'

type QuestionnairesCardProps = {
  companyId: string
  questionnaires: Questionnaire[]
}

type QuestionnaireWithDetails = {
  name: string
  commentCount: number
  flaggedAnswersCount: number
}

export const QuestionnairesCard = ({ questionnaires }: QuestionnairesCardProps) => {
  const trackingContext = useAssessmentAnalytics()
  const { updateParam } = useUrlParams()
  const trackTabShortcutClick = useTrackCallback(
    'third-party.assessment.overview.tab-shortcut.click',
  )

  const {
    completedQuestionnairesCount,
    ongoingQuestionnairesCount,
    submittedQuestionnairesCount,
    inProgressCommentCount,
    flaggedAnswersCount,
    inProgressQuestionnaires,
    completedQuestionnaires,
    submittedQuestionnaires,
  } = questionnaires.reduce(
    (acc, questionnaire) => {
      if (
        questionnaire?.base?.type !== QuestionnaireType.INFO ||
        questionnaire.questionnaire.case !== 'manualQuestionnaire'
      )
        return acc

      const questionnaireDetails: QuestionnaireWithDetails = {
        name: questionnaire.base?.name || 'Untitled',
        commentCount: questionnaire.questionnaire.value.totalAwaitingComments,
        flaggedAnswersCount: questionnaire.questionnaire.value.totalFlaggedAnswers,
      }

      switch (questionnaire?.base?.status) {
        case QuestionnaireStatus.COMPLETED:
          acc.completedQuestionnairesCount++
          acc.completedQuestionnaires.push(questionnaireDetails)
          break
        case QuestionnaireStatus.ONGOING:
          acc.ongoingQuestionnairesCount++
          acc.inProgressCommentCount += questionnaire.questionnaire.value.totalAwaitingComments
          acc.flaggedAnswersCount += questionnaire.questionnaire.value.totalFlaggedAnswers
          acc.inProgressQuestionnaires.push(questionnaireDetails)
          break
        case QuestionnaireStatus.SUBMITTED:
          acc.submittedQuestionnairesCount++
          acc.submittedQuestionnaires.push(questionnaireDetails)
          break
        default:
          break
      }
      return acc
    },
    {
      completedQuestionnairesCount: 0,
      ongoingQuestionnairesCount: 0,
      submittedQuestionnairesCount: 0,
      inProgressCommentCount: 0,
      flaggedAnswersCount: 0,
      inProgressQuestionnaires: [] as QuestionnaireWithDetails[],
      completedQuestionnaires: [] as QuestionnaireWithDetails[],
      submittedQuestionnaires: [] as QuestionnaireWithDetails[],
    },
  )

  const totalQuestionnairesCount =
    ongoingQuestionnairesCount + completedQuestionnairesCount + submittedQuestionnairesCount
  const hasQuestionnaires = totalQuestionnairesCount > 0
  const actionLabel = hasQuestionnaires ? 'Manage questionnaires' : 'Send a questionnaire'

  // Build tooltip content
  const tooltipContent = hasQuestionnaires ? (
    <div className='flex flex-col gap-3 p-2'>
      {inProgressQuestionnaires.length > 0 && (
        <div className='flex flex-col gap-1'>
          <div className='mb-1 font-semibold text-label-small'>In progress</div>
          {inProgressQuestionnaires.map((q, idx) => (
            <div key={idx} className=''>
              <div className='text-body-small'>{q.name}</div>
              <div className='my-1 flex items-center gap-2'>
                {q.commentCount > 0 && (
                  <Badge variant='neutral'>
                    <TextWithIcon
                      text={`${q.commentCount} ${pluralize('comment', q.commentCount)}`}
                      icon={<MessageSquareIcon size={14} />}
                    />
                  </Badge>
                )}
                {q.flaggedAnswersCount > 0 && (
                  <Badge variant='warning'>
                    <TextWithIcon
                      text={`${q.flaggedAnswersCount} flagged ${pluralize('answer', q.flaggedAnswersCount)}`}
                      icon={<FlagIcon size={14} />}
                    />
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {submittedQuestionnaires.length > 0 && (
        <div className='flex flex-col gap-1'>
          <div className='font-semibold text-label-small'>Submitted</div>
          {submittedQuestionnaires.map((q, idx) => (
            <div key={idx} className='text-body-small'>
              {q.name}
            </div>
          ))}
        </div>
      )}
      {completedQuestionnaires.length > 0 && (
        <>
          <Separator />
          <div className='flex flex-col gap-1'>
            <div className='font-semibold text-label-small'>Finalized</div>
            {completedQuestionnaires.map((q, idx) => (
              <div key={idx} className='text-body-small'>
                {q.name}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  ) : undefined

  return (
    <OverviewCard
      variant={hasQuestionnaires ? 'default' : 'disabled'}
      title='Questionnaires'
      mainNumber={totalQuestionnairesCount}
      details={[
        ongoingQuestionnairesCount > 0 && (
          <div key='in-progress' className='flex items-center gap-2'>
            <div>{ongoingQuestionnairesCount} In progress</div>
            {inProgressCommentCount > 0 && (
              <Badge variant='neutral'>
                <TextWithIcon
                  text={inProgressCommentCount}
                  icon={<MessageSquareIcon size={14} />}
                />
              </Badge>
            )}
            {flaggedAnswersCount > 0 && (
              <Badge variant='warning'>
                <TextWithIcon text={flaggedAnswersCount} icon={<FlagIcon size={14} />} />
              </Badge>
            )}
          </div>
        ),
        submittedQuestionnairesCount > 0 && `${submittedQuestionnairesCount} Submitted`,
        completedQuestionnairesCount > 0 && (
          <div key='finalized' className='text-main-foreground-placeholder'>
            {completedQuestionnairesCount} Finalized
          </div>
        ),
        !hasQuestionnaires && 'No questionnaires published yet.',
      ].filter(Boolean)}
      tooltip={tooltipContent}
      action={{
        label: actionLabel,
        onClick: () => {
          if (trackingContext) {
            trackTabShortcutClick({
              ...trackingContext,
              destination: 'questionnaires',
              label: actionLabel,
            })
          }

          updateParam(UrlParam.ASSESSMENT_TAB, ASSESSMENT_TABS.QUESTIONNAIRES)
        },
      }}
    />
  )
}
